import CONST_STORAGE from 'constants/storage';
import { signOut } from 'firebase/auth';
import { auth } from 'services/firebase';

import { SeatsInterface } from 'store/ducks/itineraries/types';
import { SEAT_FARE_KEY_BY_SEAT_TYPE } from 'common/lineForm.interface';
import { BOARDING_TAX_TYPE } from 'constants/ticketSales';
import { ItineraryListInterface } from 'pages/public/TicketSales/pages/TicketDetails';

export const setStorageStartStop = (item: string) =>
  localStorage.setItem(CONST_STORAGE.TICKET_SALES.STAR_STOP, item);

export const getStorageStartStop = () =>
  localStorage.getItem(CONST_STORAGE.TICKET_SALES.STAR_STOP) ||
  JSON.stringify([]);

export const setStorageEndStop = (item: string) =>
  localStorage.setItem(CONST_STORAGE.TICKET_SALES.END_STOP, item);

export const getStorageEndStop = () =>
  localStorage.getItem(CONST_STORAGE.TICKET_SALES.END_STOP) ||
  JSON.stringify([]);

const parseStoredStops = (raw: string) => {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

export const findStoredStopWithAddress = (stopId?: string) => {
  if (!stopId) return null;
  const storedStops = [
    ...parseStoredStops(getStorageStartStop()),
    ...parseStoredStops(getStorageEndStop()),
  ];
  return (
    storedStops.find((stop) => stop?.id === stopId && stop?.Address) || null
  );
};

export const upsertStoredStopWithAddress = (stop: {
  id?: string;
  Address?: unknown;
}) => {
  if (!stop?.id || !stop.Address) return;

  const updateList = (
    raw: string,
    setter: (value: string) => void,
  ): boolean => {
    const list = parseStoredStops(raw);
    const index = list.findIndex((item) => item?.id === stop.id);
    if (index < 0) return false;
    list[index] = stop;
    setter(JSON.stringify(list));
    return true;
  };

  const updatedStart = updateList(getStorageStartStop(), setStorageStartStop);
  const updatedEnd = updateList(getStorageEndStop(), setStorageEndStop);

  if (!updatedStart && !updatedEnd) {
    setStorageStartStop(
      JSON.stringify([...parseStoredStops(getStorageStartStop()), stop]),
    );
  }
};

export const setStorageUserToken = (item: string) =>
  localStorage.setItem(
    CONST_STORAGE.TICKET_SALES.CURRENT_USER_TOKEN,
    `Bearer ${item}`,
  );

export const getStorageUserToken = () =>
  localStorage.getItem(CONST_STORAGE.TICKET_SALES.CURRENT_USER_TOKEN) || '';

export const setStorageUserClaims = (item: string) =>
  localStorage.setItem(CONST_STORAGE.TICKET_SALES.CURRENT_USER_CLAIMS, item);

export const getStorageUserClaims = () =>
  localStorage.getItem(CONST_STORAGE.TICKET_SALES.CURRENT_USER_CLAIMS) ||
  JSON.stringify({});

export const setStorageLineList = (item: string) =>
  localStorage.setItem(CONST_STORAGE.TICKET_SALES.CURRENT_LINE_LIST, item);

export const getStorageLineList = () =>
  localStorage.getItem(CONST_STORAGE.TICKET_SALES.CURRENT_LINE_LIST) ||
  JSON.stringify([]);

export const handleLogout = (setCurrState: (item: boolean) => void) => {
  signOut(auth);

  setCurrState(false);
  localStorage.removeItem(CONST_STORAGE.TICKET_SALES.CURRENT_USER_TOKEN);
  localStorage.removeItem(CONST_STORAGE.TICKET_SALES.CURRENT_USER_CLAIMS);
};

export const getStorageLastPath = () =>
  localStorage.getItem(CONST_STORAGE.TICKET_SALES.LAST_PATH) || '';

export const setStorageLastPath = (item: string) =>
  localStorage.setItem(CONST_STORAGE.TICKET_SALES.LAST_PATH, item);

export const maxSeat = (seats: SeatsInterface[]) =>
  seats && seats.length > 0
    ? seats.reduce<number>(
        (max, seat) =>
          Math.max(max, typeof seat.seatType === 'number' ? seat.seatType : 0),
        0,
      )
    : 0;

/**
 * Taxa de embarque da viagem no itineraryResume.
 * O campo boardingTax já vem como valor monetário resolvido pela API (ex.: 0,25),
 * igual ao usado na listagem normal — não reinterpretar como percentual.
 */
export const getTripBoardingTaxAmount = (
  trip?: {
    boardingTax?: number | null;
    boardingTaxRules?: {
      type?: string;
      values?: Array<{ value?: number; valueType?: string }>;
    };
    fare?: number;
  } | null,
) => {
  if (!trip) return 0;

  if (trip.boardingTax != null && !Number.isNaN(Number(trip.boardingTax))) {
    return Number(Number(trip.boardingTax).toFixed(2));
  }

  const ruleValue = trip.boardingTaxRules?.values?.[0];
  if (ruleValue?.value == null || Number.isNaN(Number(ruleValue.value))) {
    return 0;
  }

  const value = Number(ruleValue.value);
  const isPercentage =
    trip.boardingTaxRules?.type === BOARDING_TAX_TYPE.PERCENTAGE ||
    ruleValue.valueType === BOARDING_TAX_TYPE.PERCENTAGE;

  if (isPercentage) {
    return Number(((value / 100) * (trip.fare || 0)).toFixed(2));
  }

  return Number(value.toFixed(2));
};

export const calcSeatFare = (
  trip?: ItineraryListInterface | null,
  seatType?: number,
  holdTokens?: { fare: number; fareTax: number; isFree: boolean },
  includeBoardingTax = true,
) => {
  if (trip) {
    const boardingTax =
      includeBoardingTax && !holdTokens?.isFree
        ? getTripBoardingTaxAmount(trip)
        : 0;
    const seatFare =
      trip?.seatFares?.[
        SEAT_FARE_KEY_BY_SEAT_TYPE[
          String(
            seatType ?? String(maxSeat(trip.seats.filter((e) => e.enabled))),
          ) as keyof typeof SEAT_FARE_KEY_BY_SEAT_TYPE
        ]
      ];
    if (holdTokens && seatFare) {
      if (seatFare?.type === 'FIXED_VALUE') {
        return Number(
          (
            seatFare?.value +
            holdTokens?.fare +
            holdTokens?.fareTax +
            boardingTax
          ).toFixed(2),
        );
      } else {
        return Number(
          (
            (seatFare?.value * holdTokens?.fare) / 100 +
            holdTokens?.fare +
            holdTokens?.fareTax +
            boardingTax
          ).toFixed(2),
        );
      }
    } else if (seatFare) {
      if (seatFare?.type === 'FIXED_VALUE') {
        return Number(
          (seatFare?.value + trip?.fare + trip?.fareTax + boardingTax).toFixed(
            2,
          ),
        );
      } else {
        return Number(
          (
            (seatFare?.value * trip?.fare) / 100 +
            trip?.fare +
            trip?.fareTax +
            boardingTax
          ).toFixed(2),
        );
      }
    }
    return Number((trip?.fare + trip?.fareTax + boardingTax).toFixed(2));
  }

  return 0;
};

/**
 * Diferença no reagendamento:
 * mesmo valor da listagem normal (calcSeatFare) - valor da passagem antiga.
 */
export const calcReschedulePriceDifference = (
  trip?: ItineraryListInterface | null,
  previousAmount = 0,
  seatType?: number,
) => {
  const currentTripValue = calcSeatFare(trip, seatType);
  return Number((currentTripValue - (previousAmount || 0)).toFixed(2));
};

export const holdTokensReduced = (
  list?: {
    holdToken?: string;
    tripScheduleSeatId?: string;
    profileId?: string;
    value?: number;
    floor?: number;
    type?: number;
    seatNumber?: number;
  }[],
) =>
  list
    ? Object.values(
        list?.reduce(
          (
            acc: { [key: string]: { key: string; passengers: number } },
            curr,
          ) => {
            if (curr.profileId) {
              if (acc[curr.profileId] === undefined) {
                acc[curr.profileId] = {
                  key: curr.profileId,
                  passengers: 1,
                };
              }
              acc[curr.profileId] = {
                key: curr.profileId,
                passengers: acc[curr.profileId].passengers + 1,
              };
            }
            return acc;
          },
          {},
        ),
      )
    : [];

export const validationLicense = (license: string) => {
  const regexLicense = /^[a-zA-Z]{3}[0-9]{4}$/;
  const regexLicenseMercosulCar = /^[a-zA-Z]{3}[0-9]{1}[a-zA-Z]{1}[0-9]{2}$/;
  const regexLicenseMercosulMotocycle = /^[a-zA-Z]{3}[0-9]{2}[a-zA-Z]{1}[0-9]{1}$/;

  if (
    regexLicense.test(license) ||
    regexLicenseMercosulCar.test(license) ||
    regexLicenseMercosulMotocycle.test(license)
  ) {
    return true;
  }
  return false;
};
